/**
 * Language matching and display names shared by the CRM and the storefront.
 *
 * Chinese is offered as two languages, one per writing system: Simplified
 * (`zh-Hans`) and Traditional (`zh-Hant`). Browsers report Chinese as a
 * region (`zh-CN`, `zh-TW`, `zh-HK`) far more often than as a script, and
 * keeping only the part before the dash turned every one of them into `zh`,
 * which matches neither language. The helpers here map a locale to the
 * writing system its readers use, and give pickers a name each reader
 * recognizes whatever language the screen is currently in.
 */

/** A tenant language as the language endpoints return it. */
export interface LanguageChoiceModel {
	code: string;
	name?: string | null;
}

/** How a picker shows one language. */
export interface LanguageDisplayModel {
	/**
	 * The language's name in its own language and script (English, 简体中文,
	 * 繁體中文, 日本語, Español) so a reader can find their language on a screen
	 * they cannot read. Every language menu uses this, so all of them read the
	 * same way (PD-16672, owner decision 2026-09-30). Languages without a known
	 * native name fall back to the tenant's stored name, then the code.
	 */
	nativeName: string;
	/**
	 * The name in English, for staff-facing places that show it beside the
	 * native name: the tenant's stored name, else a known English name, else
	 * the code.
	 */
	englishName: string;
}

export const SIMPLIFIED_CHINESE_CODE = 'zh-Hans';
export const TRADITIONAL_CHINESE_CODE = 'zh-Hant';

type ChineseScriptCode =
	| typeof SIMPLIFIED_CHINESE_CODE
	| typeof TRADITIONAL_CHINESE_CODE;

/** Regions whose Chinese readers use Traditional characters. */
const TRADITIONAL_CHINESE_REGIONS: ReadonlySet<string> = new Set([
	'tw',
	'hk',
	'mo',
]);

const CHINESE_NAMES: Record<
	ChineseScriptCode,
	{ nativeName: string; englishName: string }
> = {
	[SIMPLIFIED_CHINESE_CODE]: {
		nativeName: '简体中文',
		englishName: 'Simplified Chinese',
	},
	[TRADITIONAL_CHINESE_CODE]: {
		nativeName: '繁體中文',
		englishName: 'Traditional Chinese',
	},
};

// Native and English names for the languages the New Language list offers,
// keyed by base language code. A Map, so a code such as `constructor` never
// resolves to an Object.prototype member.
const LANGUAGE_NAMES: ReadonlyMap<
	string,
	{ nativeName: string; englishName: string }
> = new Map([
	['en', { nativeName: 'English', englishName: 'English' }],
	['ja', { nativeName: '日本語', englishName: 'Japanese' }],
	['ko', { nativeName: '한국어', englishName: 'Korean' }],
	['hi', { nativeName: 'हिन्दी', englishName: 'Hindi' }],
	['bn', { nativeName: 'বাংলা', englishName: 'Bengali' }],
	['ru', { nativeName: 'Русский', englishName: 'Russian' }],
	['es', { nativeName: 'Español', englishName: 'Spanish' }],
	['pt', { nativeName: 'Português', englishName: 'Portuguese' }],
]);

/** Lower-cased subtags of a BCP 47 tag, accepting `_` as a separator too. */
function toSubtags(locale: string): Array<string> {
	return locale.trim().toLowerCase().replace(/_/g, '-').split('-');
}

function baseLanguageOf(locale: string): string {
	return toSubtags(locale)[0] ?? '';
}

function sameCode(a: string, b: string): boolean {
	return toSubtags(a).join('-') === toSubtags(b).join('-');
}

/**
 * The Chinese writing system a locale is read in, or null when the locale is
 * not Chinese. An explicit script subtag decides (`zh-Hant-CN` is
 * Traditional). Otherwise the region does: Taiwan, Hong Kong and Macau read
 * Traditional, and everywhere else, including a bare `zh`, reads Simplified.
 */
export function getChineseScriptCode(locale: string): ChineseScriptCode | null {
	const subtags = toSubtags(locale);

	if (subtags[0] !== 'zh') {
		return null;
	}

	if (subtags.includes('hant')) {
		return TRADITIONAL_CHINESE_CODE;
	}

	if (subtags.includes('hans')) {
		return SIMPLIFIED_CHINESE_CODE;
	}

	const regions = subtags.slice(1);

	return regions.some((region) => TRADITIONAL_CHINESE_REGIONS.has(region))
		? TRADITIONAL_CHINESE_CODE
		: SIMPLIFIED_CHINESE_CODE;
}

/**
 * The tenant language to show someone whose browser asks for
 * `requestedLocales` (most preferred first, as in `navigator.languages`).
 *
 * Each requested locale is tried in turn, and the first one that matches any
 * available language wins:
 *   1. the same code (`zh-Hant` → `zh-Hant`, `en-US` → `en-US`);
 *   2. for Chinese, a language in the same writing system (`zh-TW` →
 *      `zh-Hant`, `zh-CN` → `zh-Hans`);
 *   3. the same base language (`ko-KR` → `ko`, `en-GB` → `en-US`). For a
 *      Chinese reader whose own script is not offered this lands on the other
 *      one, which they can still read, rather than on the default.
 *
 * `languages` is the tenant's active languages. When that list could not be
 * loaded (null), the locale is reduced to the code a tenant would use for it:
 * the Chinese writing system, otherwise the base language. The server answers
 * an unknown code with its default language, so that guess is safe.
 *
 * Falls back to `defaultCode` when nothing matches.
 */
export function resolvePreferredLanguageCode(params: {
	requestedLocales: ReadonlyArray<string | null | undefined>;
	languages: ReadonlyArray<LanguageChoiceModel> | null;
	defaultCode: string;
}): string {
	const { requestedLocales, languages, defaultCode } = params;

	for (const locale of requestedLocales) {
		if (locale == null || locale.trim() === '') {
			continue;
		}

		const scriptCode = getChineseScriptCode(locale);

		if (languages == null) {
			return scriptCode ?? baseLanguageOf(locale);
		}

		const exact = languages.find((language) => sameCode(language.code, locale));

		if (exact != null) {
			return exact.code;
		}

		if (scriptCode != null) {
			const sameScript =
				languages.find((language) => sameCode(language.code, scriptCode)) ??
				languages.find(
					(language) => getChineseScriptCode(language.code) === scriptCode,
				);

			if (sameScript != null) {
				return sameScript.code;
			}
		}

		const base = baseLanguageOf(locale);
		const sameBase = languages.find(
			(language) => baseLanguageOf(language.code) === base,
		);

		if (sameBase != null) {
			return sameBase.code;
		}
	}

	return defaultCode;
}

/**
 * How pickers name a language: its own-language name for everyone, and an
 * English name for staff. Chinese goes by writing system, so `zh-TW` reads
 * as Traditional Chinese.
 */
export function getLanguageDisplay(
	language: LanguageChoiceModel,
): LanguageDisplayModel {
	const storedName = language.name?.trim() ?? '';
	const scriptCode = getChineseScriptCode(language.code);
	const known =
		scriptCode != null
			? CHINESE_NAMES[scriptCode]
			: LANGUAGE_NAMES.get(baseLanguageOf(language.code));
	const fallback = storedName !== '' ? storedName : language.code;

	return {
		nativeName: known?.nativeName ?? fallback,
		englishName:
			storedName !== '' ? storedName : (known?.englishName ?? language.code),
	};
}
